// PricingPage.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Grid,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    TextField,
    Button,
    Box,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    ListItemIcon,
    AppBar,
    Toolbar,
    IconButton,
    Switch,
    useTheme,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import PricingCard from './PricingCard';
import {
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import ModeToggler from 'src/@core/layouts/components/shared-components/ModeToggler'

import { useSettings } from 'src/@core/hooks/useSettings';
import axios from 'src/services/ApiService';
import { useNavigate } from 'react-router-dom'; // Replace with your router import
import AuthorizeService from '../../src/components/api-authorization/AuthorizeService';

export default function PricingPage() {
    const [dataList, setDataList] = useState(null);
    const [features, setFeatures] = useState(null);
    const [alignment, setAlignment] = useState('monthly');
    const [count, setCount] = useState(1);
    const [featureList, setFeatureList] = useState([
        'Planning & publishing tools',
        'Landing page builder',
        'AI Assistant',
    ]);
    const [userId, setUserId] = useState(null);
    const [amount, setAmount] = useState(0);
    const amountEssentials = useRef(0);
    const amountTeam = useRef(0);
    const amountAgency = useRef(0);
    const navigate = useNavigate();

    const theme = useTheme();
    const { settings, saveSettings } = useSettings();
    const { mode } = settings;
    const handleModeToggle = () => {
        saveSettings({ ...settings, mode: mode === 'light' ? 'dark' : 'light' });
    };

    const handleChange = (event, newAlignment) => {
        if (newAlignment !== null) {
            setAlignment(newAlignment);
        }
    };

    const increment = () => {
        setCount(count + 1);
    };

    const decrement = () => {
        if (count > 1) {
            setCount(count - 1);
        }
    };

    useEffect(() => {
        const fetchSubscriptions = async () => {
            try {
                const response = await axios.get('Subscription');
                const dataList = response.data.map((item) => ({
                    id: item.id,
                    name: item.name,
                    monthlyCost: item.monthlyCost,
                    yearlyCost: item.yearlyCost,
                    channelsLimit: item.channelsLimit,
                    channelsPrice: item.channelsPrice,
                    //description: item.description, 
                }));

                const featuresList = response.data.map((item) => ({
                    'Posts Per Month': item.postsPerMonth,
                    'Can Schedule Post': item.canSchedulePost,
                    Instagram: item.instagram,
                    TikTok: item.tikTok,
                    Facebook: item.facebook,
                    Twitter: item.twitter,
                    LinkedIn: item.linkedIn,
                    Threads: item.threads,
                    Bluesky: item.bluesky,
                    Mastodon: item.mastodon,
                    YouTube: item.youTube,
                    Google: item.google,
                    Pinterest: item.pinterest,
                    'AI Assistant': item.aiAssistant,
                    Canva: item.canva,
                    'Post Previews': item.postPreviews,
                    'Content Scheduling': item.contentScheduling,
                    'Hashtag Manager': item.hashtagManager,
                    'Link Shorteners': item.linkShorteners,
                }));

                setDataList(dataList);
                setFeatures(featuresList);
            } catch (err) {
                console.log(err);
            }
        };

        const getUserDetails = async () => {
            const response = await AuthorizeService.getUser();
            if (response) {
                setUserId(response.sub);
            } else {
                setUserId(null);
            }
        };

        fetchSubscriptions();
        getUserDetails();
    }, []);

    const renderPricing = (planName) => {
        let planAmount = 0;
        if (planName === 'Free') {
            return (
                <>
                    <Typography variant="h4" component="span">
                        $0
                    </Typography>
                    <Typography variant="body2">Connect up to 3 channels</Typography>
                </>
            );
        }

        if (planName === 'Essentials') {
            let incrementValue = alignment === 'monthly' ? 6 : 5;
            amountEssentials.current = incrementValue * count;
            return (
                <>
                    <Typography variant="h4" component="span">
                        ${incrementValue * count}
                    </Typography>
                    <Typography variant="body2">
                        / {alignment} for {count} channel(s)
                    </Typography>
                </>
            );
        }

        if (planName === 'Team') {
            let incrementValue = alignment === 'monthly' ? 12 : 10;
            amountTeam.current = incrementValue * count;
            return (
                <>
                    <Typography variant="h4" component="span">
                        ${incrementValue * count}
                    </Typography>
                    <Typography variant="body2">
                        / {alignment} for {count} channel(s)
                    </Typography>
                </>
            );
        }

        if (planName === 'Agency') {
            let basePrice = alignment === 'monthly' ? 120 : 100;
            let amount = count <= 10 ? basePrice : basePrice + basePrice * (count - 10);
            amountAgency.current = amount;
            return (
                <>
                    <Typography variant="h4" component="span">
                        ${amount}
                    </Typography>
                    <Typography variant="body2">
                        / {alignment} for {count} channel(s)
                    </Typography>
                </>
            );
        }
        setAmount(planAmount);
    };

    const handleSubPlan = async (id, planName) => {
        const subscriptionId = id;
        let planPrice =
            planName === 'Essentials'
                ? amountEssentials.current
                : planName === 'Team'
                    ? amountTeam.current
                    : planName === 'Agency'
                        ? amountAgency.current
                        : 0;
        sessionStorage.setItem('subscriptionId', subscriptionId);
        sessionStorage.setItem('planName', planName);
        sessionStorage.setItem('planPrice', planPrice.toString());
        if (!userId) {
            console.log('User is not logged in');
            navigate(`/authentication/login`);
        } else {
            navigate('/payment');
        }
    };

    return (
        <>
            <AppBar position="static" color="transparent" elevation={0}>
                <Toolbar>
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
                            <ArrowBackIcon />
                        </IconButton>

                        {/*<Typography variant='h5' sx={{ ml: 2, fontWeight: 500, lineHeight: 1.2, flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate('/')}>*/}
                        {/*    SocialOneApp*/}
                        {/*</Typography>*/}
                    </Box>

                    {/* Dark Mode Toggle */}
                {/*<ModeToggler settings={settings} saveSettings={saveSettings} />*/}

                </Toolbar>
            </AppBar>

        <Container maxWidth="lg" sx={{ py: 8 }}>
            <Typography variant="h3" align="center" gutterBottom>
                Pick your perfect plan
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    mt: 4,
                    mb: 6,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={decrement}
                        sx={{ minWidth: 40 }}
                    >
                        -
                    </Button>
                    <TextField
                        value={count}
                        variant="outlined"
                        size="small"
                        sx={{ width: 60, mx: 1 }}
                        inputProps={{
                            style: {
                                textAlign: 'center',
                                fontWeight: 'bold',
                                fontSize: '16px',
                            },
                        }}
                    />
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={increment}
                        sx={{ minWidth: 40 }}
                    >
                        +
                    </Button>
                </Box>
                <ToggleButtonGroup
                    color="primary"
                    value={alignment}
                    exclusive
                    onChange={handleChange}
                    aria-label="billing period"
                >
                    <ToggleButton value="monthly">Monthly</ToggleButton>
                    <ToggleButton value="yearly">Yearly</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Grid container spacing={4}>
                {dataList &&
                    dataList.map((plan, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <PricingCard
                                plan={plan}
                                alignment={alignment}
                                count={count}
                                renderPricing={renderPricing}
                                handleSubPlan={handleSubPlan}
                                isFree={plan.name === 'Free'}
                                popular={plan.name === 'Essentials'}
                                featureList={featureList}
                            />
                        </Grid>
                    ))}
            </Grid>
            <Typography variant="h4" align="center" gutterBottom sx={{ mt: 8 }}>
                Compare our plans and features
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 4, maxHeight: '60vh' }}>
                <Table stickyHeader aria-label="feature comparison table">
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            {dataList &&
                                dataList.map((plan, index) => (
                                    <TableCell key={index} align="center">
                                        <Typography variant="h6">{plan.name}</Typography>
                                        
                                    </TableCell>
                                ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {features &&
                            Object.keys(features[0]).map((featureKey, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    <TableCell component="th" scope="row">
                                        {featureKey}
                                    </TableCell>
                                    {features.map((feature, colIndex) => (
                                        <TableCell key={colIndex} align="center">
                                            {typeof feature[featureKey] === 'boolean' ? (
                                                feature[featureKey] ? (
                                                    <CheckIcon color="success" />
                                                ) : (
                                                    ''
                                                )
                                            ) : (
                                                feature[featureKey]
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
        </>
    );
}
